import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { 
  CreditCard, ChevronLeft, ChevronRight, User, 
  Calendar, DollarSign, RefreshCw, Filter, Undo2
} from 'lucide-react';
import { adminFetch } from './AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const statusColors = {
  paid: 'bg-green-600/20 text-green-400',
  pending: 'bg-yellow-600/20 text-yellow-400',
  failed: 'bg-red-600/20 text-red-400',
  refunded: 'bg-purple-600/20 text-purple-400',
};

const statusDescriptions = {
  paid: 'Payment completed successfully',
  pending: 'Payment initiated but not yet confirmed. User may have abandoned checkout or payment is still processing.',
  failed: 'Payment was declined or failed',
  refunded: 'Payment has been refunded to the customer',
};

const AdminTransactions = () => {
  const { adminToken } = useOutletContext();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [refundModal, setRefundModal] = useState({ show: false, transaction: null, reason: '' });
  const limit = 20;

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * limit, limit });
      if (statusFilter) params.append('status', statusFilter);
      
      const data = await adminFetch(`/admin/transactions?${params}`, {}, adminToken);
      setTransactions(data.transactions);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, statusFilter, adminToken]);

  const handleRefund = async () => {
    if (!refundModal.transaction) return;
    
    try {
      await adminFetch('/admin/refund', {
        method: 'POST',
        body: JSON.stringify({
          transaction_id: refundModal.transaction.transaction_id,
          reason: refundModal.reason || 'Admin refund'
        })
      }, adminToken);
      
      toast.success('Refund processed');
      setRefundModal({ show: false, transaction: null, reason: '' });
      fetchTransactions();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Transactions</h1>
          <p className="text-gray-400 mt-1">{total} total transactions</p>
        </div>
        <Button onClick={fetchTransactions} variant="outline" className="border-gray-600">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter size={18} className="text-gray-400" />
        {['', 'paid', 'pending', 'failed'].map((status) => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setPage(0); }}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              statusFilter === status
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {status || 'All'}
          </button>
        ))}
      </div>

      {/* Transactions Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Package</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Credits</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.transaction_id} className="hover:bg-gray-700/30" data-testid={`tx-row-${tx.transaction_id}`}>
                    <td className="px-4 py-3 text-gray-300">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {tx.user_info ? (
                        <Link 
                          to={`/admin/users/${tx.user_id}`}
                          className="text-emerald-400 hover:underline"
                        >
                          {tx.user_info.name || tx.user_info.email}
                        </Link>
                      ) : (
                        <span className="text-gray-400">Unknown</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white capitalize">
                      {tx.package_id || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-white">
                      ${tx.amount?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-4 py-3 text-purple-400">
                      +{tx.credits || 0}
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative group">
                        <span className={`px-2 py-1 rounded-full text-xs cursor-help ${
                          tx.refunded 
                            ? statusColors.refunded 
                            : statusColors[tx.payment_status] || statusColors.pending
                        }`}>
                          {tx.refunded ? 'refunded' : tx.payment_status || 'pending'}
                        </span>
                        {/* Status tooltip */}
                        <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-gray-200 text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-xs">
                          {tx.refunded 
                            ? statusDescriptions.refunded 
                            : statusDescriptions[tx.payment_status] || statusDescriptions.pending}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {tx.payment_status === 'paid' && !tx.refunded && (
                        <button
                          onClick={() => setRefundModal({ show: true, transaction: tx, reason: '' })}
                          className="p-2 hover:bg-gray-600 rounded-lg text-orange-400 transition-colors"
                          title="Refund"
                        >
                          <Undo2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
                className="border-gray-600"
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-gray-400 text-sm px-2">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="border-gray-600"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Refund Modal */}
      {refundModal.show && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Process Refund</h3>
            
            <div className="space-y-4">
              <div className="bg-gray-700/50 p-4 rounded-lg">
                <p className="text-gray-400 text-sm">Transaction</p>
                <p className="text-white">{refundModal.transaction?.transaction_id}</p>
                <p className="text-emerald-400 text-lg mt-1">
                  ${refundModal.transaction?.amount?.toFixed(2)} • {refundModal.transaction?.credits} credits
                </p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Reason</label>
                <Input
                  value={refundModal.reason}
                  onChange={(e) => setRefundModal(m => ({ ...m, reason: e.target.value }))}
                  placeholder="Refund reason..."
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              <div className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-3">
                <p className="text-orange-400 text-sm">
                  This will mark the transaction as refunded and deduct {refundModal.transaction?.credits} credits from the user.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button 
                variant="outline" 
                onClick={() => setRefundModal({ show: false, transaction: null, reason: '' })}
                className="border-gray-600"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRefund}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Process Refund
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactions;
